import { useState, useEffect, useCallback } from 'react';
import { MaintenanceStatus } from '../types';
import { adminApiService } from '../services/adminApi';
import { ApiError } from '../services/api';

interface MaintenanceState {
  status: MaintenanceStatus | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

interface MaintenanceActions {
  refreshStatus: () => Promise<void>;
  enableMaintenance: (message?: string) => Promise<boolean>;
  disableMaintenance: () => Promise<boolean>;
  clearError: () => void;
}

interface MaintenanceHookReturn extends MaintenanceState, MaintenanceActions {
  isEnabled: boolean;
  canToggle: boolean;
}

export function useMaintenanceMode(autoRefresh = false): MaintenanceHookReturn {
  const [state, setState] = useState<MaintenanceState>({
    status: null,
    loading: true,
    error: null,
    lastUpdated: null,
  });

  // Refresh maintenance status
  const refreshStatus = useCallback(async (): Promise<void> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const status = await adminApiService.getMaintenanceStatus();
      setState(prev => ({
        ...prev,
        status,
        loading: false,
        lastUpdated: new Date(),
      }));
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to fetch maintenance status';
      setState(prev => ({
        ...prev,
        error: errorMessage,
        loading: false,
      }));
    }
  }, []);

  // Enable maintenance mode
  const enableMaintenance = useCallback(async (message?: string): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const status = await adminApiService.enableMaintenance(message);
      setState(prev => ({
        ...prev,
        status,
        loading: false,
        lastUpdated: new Date(),
      }));
      return true;
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to enable maintenance mode';
      setState(prev => ({
        ...prev,
        error: errorMessage,
        loading: false,
      }));
      return false;
    }
  }, []);

  // Disable maintenance mode
  const disableMaintenance = useCallback(async (): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const status = await adminApiService.disableMaintenance();
      setState(prev => ({
        ...prev,
        status,
        loading: false,
        lastUpdated: new Date(),
      }));
      return true;
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to disable maintenance mode';
      setState(prev => ({
        ...prev,
        error: errorMessage,
        loading: false,
      }));
      return false;
    }
  }, []);

  // Clear error
  const clearError = useCallback((): void => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Auto-refresh setup
  useEffect(() => {
    // Initial load
    refreshStatus();

    if (autoRefresh) {
      // Refresh every 30 seconds
      const interval = setInterval(refreshStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [refreshStatus, autoRefresh]);

  // Handle page visibility change for auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshStatus, autoRefresh]);

  const isEnabled = state.status?.enabled ?? false;
  const canToggle = !state.loading && !state.error;

  return {
    ...state,
    isEnabled,
    canToggle,
    refreshStatus,
    enableMaintenance,
    disableMaintenance,
    clearError,
  };
}